// src/utils/elettrodottiCache.ts
// DOS518 – Cache IndexedDB per dati vettoriali elettrodotti (offline support)
//
// Usa un database separato da quello dei tile per non interferire con
// la tile cache esistente. TTL: 7 giorni (i dati OSM cambiano raramente).

import * as L from 'leaflet';
import { ElettrodottiGeoJSON } from './elettrodottiOverpass';

const DB_NAME    = 'dos518_elettrodotti_cache';
const STORE_NAME = 'geojson';
const DB_VERSION = 1;
const CACHE_TTL  = 7 * 24 * 60 * 60 * 1000; // 7 giorni in ms

interface CacheEntry {
  key: string;
  data: ElettrodottiGeoJSON;
  fetchedAt: number;
}

// ─── Apertura DB ──────────────────────────────────────────────────────────────

let dbPromise: Promise<IDBDatabase> | null = null;

function getDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = (e.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'key' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => {
      dbPromise = null;
      reject(req.error);
    };
  });
  return dbPromise;
}

// ─── Chiave cache ─────────────────────────────────────────────────────────────
//
// Arrotondo a griglia 0.1° (~10 km): richieste simili riusano la stessa entry.

function boundsToKey(bounds: L.LatLngBounds): string {
  const r = (n: number) => (Math.round(n * 10) / 10).toFixed(1);
  const sw = bounds.getSouthWest();
  const ne = bounds.getNorthEast();
  return `elec_${r(sw.lat)}_${r(sw.lng)}_${r(ne.lat)}_${r(ne.lng)}`;
}

// ─── API pubblica ─────────────────────────────────────────────────────────────

/** Restituisce i dati in cache se validi, null altrimenti. */
export async function getCachedElettrodotti(
  bounds: L.LatLngBounds
): Promise<ElettrodottiGeoJSON | null> {
  try {
    const db  = await getDB();
    const key = boundsToKey(bounds);

    return new Promise((resolve) => {
      const tx  = db.transaction(STORE_NAME, 'readonly');
      const req = tx.objectStore(STORE_NAME).get(key);

      req.onsuccess = () => {
        const entry: CacheEntry | undefined = req.result;
        if (!entry) { resolve(null); return; }
        if (Date.now() - entry.fetchedAt > CACHE_TTL) { resolve(null); return; }
        resolve(entry.data);
      };
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

/** Salva i dati in cache. */
export async function setCachedElettrodotti(
  bounds: L.LatLngBounds,
  data: ElettrodottiGeoJSON
): Promise<void> {
  try {
    const db  = await getDB();
    const key = boundsToKey(bounds);
    const entry: CacheEntry = { key, data, fetchedAt: Date.now() };

    await new Promise<void>((resolve, reject) => {
      const tx  = db.transaction(STORE_NAME, 'readwrite');
      const req = tx.objectStore(STORE_NAME).put(entry);
      req.onsuccess = () => resolve();
      req.onerror   = () => reject(req.error);
    });
  } catch (e) {
    console.warn('[ElettrodottiCache] Errore scrittura:', e);
  }
}

/** Svuota tutta la cache (es. da pannello impostazioni). */
export async function clearElettrodottiCache(): Promise<void> {
  try {
    const db = await getDB();
    await new Promise<void>((resolve, reject) => {
      const tx  = db.transaction(STORE_NAME, 'readwrite');
      const req = tx.objectStore(STORE_NAME).clear();
      req.onsuccess = () => resolve();
      req.onerror   = () => reject(req.error);
    });
  } catch (e) {
    console.warn('[ElettrodottiCache] Errore clear:', e);
  }
}

/** Ritorna il numero di entry in cache e l'età della più recente. */
export async function statsCacheElettrodotti(): Promise<{
  entries: number;
  oldestMs: number | null;
  newestMs: number | null;
}> {
  try {
    const db = await getDB();
    const entries = await new Promise<CacheEntry[]>((resolve) => {
      const tx  = db.transaction(STORE_NAME, 'readonly');
      const req = tx.objectStore(STORE_NAME).getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror   = () => resolve([]);
    });

    if (entries.length === 0) return { entries: 0, oldestMs: null, newestMs: null };
    const timestamps = entries.map(e => e.fetchedAt);
    return {
      entries: entries.length,
      oldestMs: Math.min(...timestamps),
      newestMs: Math.max(...timestamps),
    };
  } catch {
    return { entries: 0, oldestMs: null, newestMs: null };
  }
}
